import React, { useState, useEffect } from 'react';
import { 
  Brain, 
  Zap,
  Target, 
  BarChart3,
  Clock,
  Star,
  CheckCircle,
  ArrowRight,
  Play,
  Globe,
  Menu, 
  X,
  ChevronDown,
  Sparkles,
  Trophy,
  Users,
  Shield,
  Lightbulb,
  Cpu,
  Activity,
  TrendingUp,
  Award,
  BookOpen,
  Gamepad2,
  Timer,
  BrainCircuit
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const CONFIG = {
  PRODUCT_NAME: "IQ GameZone",
  BILINGUAL_SUPPORT: true,
  PRIMARY_LANGUAGE: { name: 'English', code: 'en', flag: 'EN' },
  SECONDARY_LANGUAGE: { name: 'Polish', code: 'pl', flag: 'PL' },
};

// Completely new bilingual content
const content = {
  en: {
    nav: {
      home: "Home",
      games: "Games",
      science: "Science",
      results: "Results",
    },
    hero: {
      badge: "🧠 #1 Brain Training Platform",
      title: "Master Your Mind",
      subtitle: "The Ultimate Cognitive Training Experience",
      description: "Revolutionary brain training that adapts to your unique cognitive profile. Boost IQ, enhance memory, and unlock peak mental performance.",
      cta: "Start Your Journey",
      stats: [
        { number: "2M+", label: "Active Users" },
        { number: "50+", label: "Brain Games" },
        { number: "95%", label: "See Results" }
      ]
    },
    features: {
      title: "Why IQ GameZone Works",
      subtitle: "Science-backed training that delivers real results",
      items: [
        {
          icon: "BrainCircuit",
          title: "Neural Adaptation",
          description: "Your brain literally rewires itself through our training protocols"
        },
        {
          icon: "Zap",
          title: "Instant Feedback",
          description: "Real-time performance analysis guides your improvement"
    },
    {
      icon: "Target",
          title: "Precision Training",
          description: "Target specific cognitive functions with surgical precision"
        },
        {
          icon: "Activity",
          title: "Adaptive Difficulty",
          description: "AI adjusts challenge level to keep you in the optimal learning zone"
        }
      ]
    },
    games: {
      title: "Cognitive Arsenal",
      subtitle: "50+ scientifically designed brain challenges",
      categories: [
        {
          name: "Memory Palace",
          icon: "BookOpen",
          games: ["Spatial Memory", "Sequence Recall", "Pattern Recognition"],
          color: "from-blue-500 to-cyan-500"
        },
        {
          name: "Logic Lab",
          icon: "Lightbulb", 
          games: ["Deductive Reasoning", "Problem Solving", "Critical Thinking"],
          color: "from-purple-500 to-pink-500"
        },
        {
          name: "Speed Zone",
          icon: "Zap",
          games: ["Reaction Time", "Processing Speed", "Quick Decisions"],
          color: "from-yellow-500 to-orange-500"
        },
        {
          name: "Focus Forge",
          icon: "Target",
          games: ["Attention Control", "Concentration", "Distraction Filtering"],
          color: "from-green-500 to-emerald-500"
        }
      ]
    },
    science: {
      title: "The Science Behind Success",
      subtitle: "Neuroscience meets cutting-edge technology",
      research: [
        {
          stat: "23%",
          description: "Average IQ increase after 3 months"
        },
        {
          stat: "40%",
          description: "Improvement in working memory"
        },
        {
          stat: "67%",
          description: "Faster problem-solving speed"
        }
      ],
      methodology: "Our training is based on neuroplasticity research and cognitive load theory, ensuring maximum brain adaptation with minimal time investment."
    },
    results: {
      title: "Real Results, Real People",
      subtitle: "See what our community has achieved",
      testimonials: [
        {
          name: "Sarah Chen",
          role: "Software Engineer",
          quote: "My problem-solving speed increased dramatically. I can now tackle complex coding challenges that used to take hours in minutes.",
          improvement: "+18 IQ points"
        },
        {
          name: "Marcus Johnson",
          role: "Medical Student",
          quote: "The memory training helped me memorize anatomy like never before. I'm acing my exams now.",
          improvement: "+15 IQ points"
        },
        {
          name: "Elena Rodriguez",
          role: "Marketing Director",
          quote: "My focus and attention span improved so much. I can now handle multiple projects without mental fatigue.",
          improvement: "+22 IQ points"
        }
      ]
    },
    cta: {
      title: "Ready to Transform Your Mind?",
      subtitle: "Join millions who've already upgraded their cognitive abilities",
      button: "Start Training Now"
    },
    footer: {
      company: "IQ GameZone",
      tagline: "Unlock Your Mental Potential",
      links: {
        privacy: "Privacy Policy",
        terms: "Terms of Service",
        support: "Support Center"
      },
      smsInfo: "One-time SMS cost is 30.75 PLN including VAT. After sending SMS you will receive a return message with a link to access the purchased content active for 30 days. By clicking the button, you agree to immediate start of service (i.e. before the expiry of the 14-day withdrawal period) from the moment of its full performance. By activating the service you confirm that you have read the Terms and Conditions.",
      provider: "Service provider: Ziller Mobile B.V Nassaukade 157H 1053 LK Amsterdam, Complaints: reklamacje@teleaudio.pl or tel: 22 244 40 22 (9-17). Domain and advertising rights are owned by Clickzillas B.V."
    }
  },
  pl: {
    nav: {
      home: "Strona Główna",
      games: "Gry",
      science: "Nauka",
      results: "Wyniki",
    },
    hero: {
      badge: "🧠 Platforma Treningu Mózgu #1",
      title: "Opanuj Swój Umysł",
      subtitle: "Ostateczne Doświadczenie Treningu Poznawczego",
      description: "Rewolucyjny trening mózgu, który dostosowuje się do Twojego unikalnego profilu poznawczego. Zwiększ IQ, wzmocnij pamięć i odblokuj szczytową wydajność umysłową.",
      cta: "Rozpocznij Swoją Podróż",
      stats: [
        { number: "2M+", label: "Aktywnych Użytkowników" },
        { number: "50+", label: "Gier Mózgowych" },
        { number: "95%", label: "Widzi Wyniki" }
      ]
    },
    features: {
      title: "Dlaczego IQ GameZone Działa",
      subtitle: "Trening oparty na nauce, który przynosi prawdziwe rezultaty",
      items: [
        {
          icon: "BrainCircuit",
          title: "Adaptacja Neuronowa",
          description: "Twój mózg dosłownie przeprogramowuje się przez nasze protokoły treningowe"
        },
        {
          icon: "Zap",
          title: "Natychmiastowa Informacja Zwrotna",
          description: "Analiza wydajności w czasie rzeczywistym prowadzi Twoje ulepszenia"
        },
        {
          icon: "Target",
          title: "Trening Precyzyjny",
          description: "Celuj w określone funkcje poznawcze z chirurgiczną precyzją"
        },
        {
          icon: "Activity",
          title: "Adaptacyjna Trudność",
          description: "AI dostosowuje poziom wyzwania, aby utrzymać Cię w optymalnej strefie uczenia"
        }
      ]
    },
    games: {
      title: "Arsenał Poznawczy",
      subtitle: "50+ naukowo zaprojektowanych wyzwań mózgowych",
      categories: [
        {
          name: "Pałac Pamięci",
          icon: "BookOpen",
          games: ["Pamięć Przestrzenna", "Przypominanie Sekwencji", "Rozpoznawanie Wzorców"],
          color: "from-blue-500 to-cyan-500"
        },
        {
          name: "Laboratorium Logiki",
          icon: "Lightbulb",
          games: ["Rozumowanie Dedukcyjne", "Rozwiązywanie Problemów", "Myślenie Krytyczne"],
          color: "from-purple-500 to-pink-500"
        },
        {
          name: "Strefa Prędkości",
          icon: "Zap",
          games: ["Czas Reakcji", "Prędkość Przetwarzania", "Szybkie Decyzje"],
          color: "from-yellow-500 to-orange-500"
        },
        {
          name: "Kuźnia Fokusa",
          icon: "Target",
          games: ["Kontrola Uwagi", "Koncentracja", "Filtrowanie Rozproszeń"],
          color: "from-green-500 to-emerald-500"
        }
      ]
    },
    science: {
      title: "Nauka Za Sukcesem",
      subtitle: "Neurobiologia spotyka najnowocześniejszą technologię",
      research: [
        {
          stat: "23%",
          description: "Średni wzrost IQ po 3 miesiącach"
        },
        {
          stat: "40%",
          description: "Poprawa pamięci roboczej"
        },
        {
          stat: "67%",
          description: "Szybsza prędkość rozwiązywania problemów"
        }
      ],
      methodology: "Nasz trening opiera się na badaniach neuroplastyczności i teorii obciążenia poznawczego, zapewniając maksymalną adaptację mózgu przy minimalnej inwestycji czasu."
    },
    results: {
      title: "Prawdziwe Wyniki, Prawdziwi Ludzie",
      subtitle: "Zobacz, co osiągnęła nasza społeczność",
      testimonials: [
        {
          name: "Sarah Chen",
          role: "Inżynier Oprogramowania",
          quote: "Moja prędkość rozwiązywania problemów dramatycznie wzrosła. Mogę teraz podejmować złożone wyzwania programistyczne, które wcześniej zajmowały godziny, w minuty.",
          improvement: "+18 punktów IQ"
        },
        {
          name: "Marcus Johnson",
          role: "Student Medycyny",
          quote: "Trening pamięci pomógł mi zapamiętać anatomię jak nigdy wcześniej. Teraz zdaję egzaminy na piątkę.",
          improvement: "+15 punktów IQ"
        },
        {
          name: "Elena Rodriguez",
          role: "Dyrektor Marketingu",
          quote: "Moja koncentracja i czas uwagi poprawiły się tak bardzo. Mogę teraz obsługiwać wiele projektów bez zmęczenia umysłowego.",
          improvement: "+22 punkty IQ"
        }
      ]
    },
    cta: {
      title: "Gotowy, aby Przekształcić Swój Umysł?",
      subtitle: "Dołącz do milionów, które już ulepszyły swoje zdolności poznawcze",
      button: "Rozpocznij Trening Teraz"
    },
    footer: {
      company: "IQ GameZone",
      tagline: "Odkryj Swój Potencjał Umysłowy",
      links: {
        privacy: "Polityka Prywatności",
        terms: "Warunki Użytkowania",
        support: "Centrum Wsparcia"
      },
      smsInfo: "Jednorazowy koszt wysłania SMS wynosi 30,75 zł z VAT. Po wysłaniu SMS otrzymasz w wiadomości zwrotnej link z dostępem do zakupionej treści aktywny przez 30 dni. Klikając przycisk, zgadzasz się na natychmiastowe rozpoczęcie świadczenia (tj. przed upływem 14-dniowego terminu na odstąpienie od niej) z chwilą jej pełnego wykonania. Aktywując usługę potwierdzasz zapoznanie się z Regulaminem.",
      provider: "Dostawca usługi: Ziller Mobile B.V Nassaukade 157H 1053 LK Amsterdam, Reklamacje: reklamacje@teleaudio.pl lub tel: 22 244 40 22 (9-17). Domain and advertising rights are owned by Clickzillas B.V."
    }
  }
};

function App() {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Handle scroll effect for header
  useEffect(() => {
    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % getContent('results').testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [currentLanguage]);

  const toggleLanguage = () => {
    setCurrentLanguage(currentLanguage === 'en' ? 'pl' : 'en');
  };

  const getContent = (key) => {
    return content[currentLanguage]?.[key] || content.en[key];
  };

  const renderIcon = (iconName) => {
    const iconMap = {
      Brain: Brain,
      Zap: Zap,
      Target: Target,
      BarChart3: BarChart3,
      Clock: Clock,
      Star: Star,
      CheckCircle: CheckCircle,
      ArrowRight: ArrowRight,
      Play: Play,
      Globe: Globe,
      Menu: Menu,
      X: X,
      ChevronDown: ChevronDown,
      Sparkles: Sparkles,
      Trophy: Trophy,
      Users: Users,
      Shield: Shield,
      Lightbulb: Lightbulb,
      Cpu: Cpu,
      Activity: Activity,
      TrendingUp: TrendingUp,
      Award: Award,
      BookOpen: BookOpen,
      Gamepad2: Gamepad2,
      Timer: Timer,
      BrainCircuit: BrainCircuit
    };
    
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="w-6 h-6" /> : null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHeaderScrolled ? 'bg-slate-900/95 backdrop-blur-md shadow-2xl' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {CONFIG.PRODUCT_NAME}
              </span>
            </div>

            <div className="hidden md:flex items-center space-x-8">
              {Object.entries(getContent('nav')).map(([key, value]) => (
                <a
                  key={key}
                  href={`#${key}`}
                  className="text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium"
                >
                  {value}
                </a>
              ))}
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={toggleLanguage}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 transition-all duration-300 hover:scale-105 group"
                title={currentLanguage === 'en' ? 'Switch to Polish' : 'Przełącz na angielski'}
              >
                {currentLanguage === 'en' ? (
                  <>
                    <ReactCountryFlag 
                      countryCode="PL" 
                      svg 
                      style={{ 
                        width: '24px', 
                        height: '18px',
                        borderRadius: '3px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      className="group-hover:scale-110 transition-transform duration-200"
                    />
                    <span className="font-medium">PL</span>
                  </>
                ) : (
                  <>
                    <ReactCountryFlag 
                      countryCode="GB" 
                      svg 
                      style={{ 
                        width: '24px', 
                        height: '18px',
                        borderRadius: '3px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                      className="group-hover:scale-110 transition-transform duration-200"
                    />
                    <span className="font-medium">EN</span>
                  </>
                )}
              </button>

              <a href="/s" className="hidden md:block bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 font-medium">
                {getContent('hero').cta}
              </a>

              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-slate-300 hover:text-cyan-400 transition-colors duration-200"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900/95 backdrop-blur-md border-t border-slate-700">
            <div className="px-4 py-2 space-y-1">
              {Object.entries(getContent('nav')).map(([key, value]) => (
                <a
                  key={key}
                  href={`#${key}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 text-slate-300 hover:text-cyan-400 transition-colors duration-200 font-medium rounded-lg"
                >
                  {value}
                </a>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 via-purple-500/20 to-blue-500/20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-cyan-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-sm font-medium mb-8">
            <Sparkles className="w-4 h-4 mr-2" />
            {getContent('hero').badge}
            </div>
          
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent leading-tight">
            {getContent('hero').title}
            </h1>
          
          <p className="text-2xl md:text-3xl text-slate-300 mb-4 font-light">
            {getContent('hero').subtitle}
          </p>
          
          <p className="text-lg md:text-xl text-slate-400 mb-12 max-w-3xl mx-auto leading-relaxed">
              {getContent('hero').description}
            </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-8 mb-16">
            {getContent('hero').stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-1">
                  {stat.number}
                </div>
                <div className="text-slate-400 text-sm">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>

          <a href="/s" className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-cyan-500/25">
            <Play className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-300" />
            {getContent('hero').cta}
            <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              {getContent('features').title}
          </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              {getContent('features').subtitle}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {getContent('features').items.map((feature, index) => (
              <div key={index} className="group relative p-8 bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700 hover:border-cyan-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative z-10">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  {renderIcon(feature.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Games Section */}
      <section className="py-20 bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              {getContent('games').title}
              </h2>
            <p className="text-xl text-slate-400 max-w-3xl mx-auto">
              {getContent('games').subtitle}
              </p>
                  </div>

          {/* Brain Games Hero Image */}
          <div className="mb-16">
            <div className="relative max-w-4xl mx-auto">
              <img 
                src="/images/brain-games-hero.png" 
                alt="Brain Training Games"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl shadow-purple-500/30"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent rounded-3xl"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  {currentLanguage === 'en' ? '50+ Scientifically Designed Games' : '50+ Naukowo Zaprojektowanych Gier'}
                </h3>
                <p className="text-slate-300 text-lg">
                  {currentLanguage === 'en' 
                    ? 'Each game targets specific cognitive functions for maximum brain improvement'
                    : 'Każda gra celuje w określone funkcje poznawcze dla maksymalnej poprawy mózgu'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {getContent('games').categories.map((category, index) => (
              <div key={index} className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 hover:border-purple-500/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className={`absolute inset-0 bg-gradient-to-br ${category.color} opacity-10 group-hover:opacity-20 transition-opacity duration-300`}></div>
                <div className="relative z-10 p-8">
                  <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {renderIcon(category.icon)}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">
                    {category.name}
                  </h3>
                  <ul className="space-y-2">
                    {category.games.map((game, gameIndex) => (
                      <li key={gameIndex} className="flex items-center text-slate-400 text-sm">
                        <CheckCircle className="w-4 h-4 mr-2 text-green-400" />
                        {game}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Science Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
                {getContent('science').title}
              </h2>
              <p className="text-xl text-slate-400 mb-8">
                {getContent('science').subtitle}
              </p>
              <p className="text-lg text-slate-300 leading-relaxed mb-8">
                {getContent('science').methodology}
              </p>
            </div>
            
            <div className="relative">
              <img 
                src="/images/ai-personalization.png" 
                alt="AI Personalization Dashboard"
                className="w-full h-80 object-cover rounded-2xl shadow-2xl shadow-green-500/30"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent rounded-2xl"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {currentLanguage === 'en' ? 'AI-Powered Personalization' : 'Personalizacja Napędzana przez AI'}
              </h3>
                <p className="text-slate-300">
                  {currentLanguage === 'en' 
                    ? 'Advanced algorithms adapt to your unique cognitive profile'
                    : 'Zaawansowane algorytmy dostosowują się do Twojego unikalnego profilu poznawczego'
                  }
                </p>
              </div>
            </div>
          </div>
          
          {/* Progress Tracking Image */}
          <div className="mt-16 mb-16">
            <div className="relative max-w-5xl mx-auto">
              <img 
                src="/images/progress-tracking.png" 
                alt="Progress Tracking Dashboard"
                className="w-full h-96 object-cover rounded-3xl shadow-2xl shadow-green-500/30"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent rounded-3xl"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-3xl font-bold text-white mb-4">
                  {currentLanguage === 'en' ? 'Track Your Cognitive Progress' : 'Śledź Swoje Postępy Poznawcze'}
                </h3>
                <p className="text-slate-300 text-lg">
                  {currentLanguage === 'en' 
                    ? 'Real-time analytics show your brain improvement over time'
                    : 'Analizy w czasie rzeczywistym pokazują poprawę Twojego mózgu w czasie'
                  }
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
            {getContent('science').research.map((item, index) => (
              <div key={index} className="text-center p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
                <div className="text-4xl font-bold text-green-400 mb-2">
                  {item.stat}
                </div>
                <div className="text-slate-400 text-sm">
                  {item.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>



      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-blue-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
            {getContent('cta').title}
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            {getContent('cta').subtitle}
          </p>
          <a href="/s" className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-semibold rounded-xl hover:from-cyan-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-2xl shadow-cyan-500/25">
            <Play className="w-5 h-5 mr-3 group-hover:scale-110 transition-transform duration-300" />
            {getContent('cta').button}
            <ArrowRight className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform duration-300" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-slate-900 border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Brain className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                {getContent('footer').company}
              </span>
            </div>
            <p className="text-slate-400 mb-8">
              {getContent('footer').tagline}
            </p>
          
          <div className="flex justify-center space-x-6 mb-8">
              <a href="/privacy.html" className="text-slate-400 hover:text-cyan-400 transition-colors duration-200">
                {getContent('footer').links.privacy}
              </a>
              <span className="text-slate-600">|</span>
              <a href="/terms.html" className="text-slate-400 hover:text-cyan-400 transition-colors duration-200">
                {getContent('footer').links.terms}
              </a>
              
          
          </div>
          
          {/* SMS Information */}
          <div className="bg-slate-800/50 rounded-lg p-6 mb-6 max-w-4xl mx-auto">
            <p className="text-slate-300 text-sm leading-relaxed mb-4">
              {getContent('footer').smsInfo}
            </p>
            <p className="text-slate-400 text-xs leading-relaxed">
              {getContent('footer').provider}
            </p>
          </div>
          
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} {CONFIG.PRODUCT_NAME}. {currentLanguage === 'en' ? 'All rights reserved.' : 'Wszystkie prawa zastrzeżone.'}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;